import type { PdfDocumentDefinition, PdfPlacement, PdfValueSource } from '../../shared/types';
import { pdfSourceType, type PdfBindingEvent } from '../../shared/pdfBindingValidation';
import { createPlacement } from './pdfEditor';
import { pdfSourceChoices, type PdfSourceChoice } from './pdfSources';

/** The field type that shows a source best. */
export function placementKindFor(event: PdfBindingEvent, document: PdfDocumentDefinition, source: PdfValueSource): PdfPlacement['kind'] | undefined {
  const type = pdfSourceType(event, document, source);
  return typeof type === 'string' ? type : undefined;
}

/** Existing answers that can fill a field of this type. Fixed text and dates and signatures are chosen their own way,
 * except the date submitted, which is a date field's first choice. */
export function pdfAnswerChoices(event: PdfBindingEvent, document: PdfDocumentDefinition, kind: PdfPlacement['kind']): PdfSourceChoice[] {
  // A field allows multiple lines when its answer needs them, so the probe does too.
  const box = { x: 0, y: 0, width: 100, height: 100 }, probe = { ...createPlacement(kind, { pageIndex: 0, visibleBox: box, mediaBox: box, cropBox: box, rotation: 0, userUnit: 1 }), multiline: kind === 'text' };
  const choices = pdfSourceChoices(event, document, probe).filter(choice => !['literal', 'signature'].includes(choice.source.kind));
  const signingDate = (c: PdfSourceChoice) => c.source.kind === 'submittedDate' || c.source.kind === 'parentSignedDate';
  return [...choices.filter(signingDate), ...choices.filter(c => !signingDate(c))];
}

/** What an answer dropdown says when nothing can fill its field yet. Only a checkbox can run out: it needs a question's
 * option or grid cell. */
export const NO_ANSWERS: Partial<Record<PdfPlacement['kind'], string>> = {
  checkbox: 'No question can fill a checkbox yet. Add a question with options, such as multiple choice or checkboxes.',
};

/** The answers as a dropdown's options, by group. A question that fills a field more than one way gets its own heading,
 * with a short name for each way. */
export function answerOptions(choices: PdfSourceChoice[]) {
  return choices.map((choice, index) => {
    const several = choice.question && choices.filter(c => c.question?.name === choice.question!.name && c.group === choice.group).length > 1;
    return { value: String(index), label: several ? `${choice.question!.name} — ${choice.question!.entry}` : choice.label, group: choice.group, ...(several ? { subgroup: choice.question!.name, menuLabel: choice.question!.entry } : {}) };
  });
}
